import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, BRAND } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Eye, EyeOff, Loader2, CheckCircle2, KeyRound, ShieldCheck } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError("Reset link is missing the token. Please request a new one.");
  }, [token]);

  const strength = useMemo(() => {
    if (!pwd) return { score: 0, label: "" };
    let s = 0;
    if (pwd.length >= 8) s++;
    if (pwd.length >= 12) s++;
    if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
    if (/\d/.test(pwd)) s++;
    if (/[^a-zA-Z0-9]/.test(pwd)) s++;
    return { score: s, label: ["", "Weak", "Fair", "Good", "Strong", "Excellent"][s] };
  }, [pwd]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (pwd.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (pwd !== pwd2) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    try {
      await api.post("/password/reset", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-secondary px-6 py-10">
      <Card className="w-full max-w-md p-8 shadow-xl">
        <div className="mb-6 flex items-center gap-3">
          <img src={BRAND.icon} alt="" className="h-10 w-10" />
          <div className="leading-tight">
            <div className="font-heading text-lg font-semibold text-brand">{BRAND.name}</div>
            <div className="text-[10px] text-ink-muted">{BRAND.tagline}</div>
          </div>
        </div>

        {done ? (
          <div data-testid="reset-success" className="space-y-4">
            <div className="grid h-12 w-12 place-items-center rounded-full bg-leaf/15 text-leaf-700"><CheckCircle2 size={24} /></div>
            <h2 className="font-heading text-2xl font-semibold">Password updated</h2>
            <p className="text-sm text-ink-muted">You can sign in now with your new password. Redirecting…</p>
            <Button onClick={() => navigate("/login", { replace: true })} className="w-full bg-brand hover:bg-brand-700" data-testid="reset-go-login">Go to sign in</Button>
          </div>
        ) : (
          <>
            <div className="mb-1 grid h-10 w-10 place-items-center rounded-full bg-brand/10 text-brand"><KeyRound size={18} /></div>
            <h2 className="mt-3 font-heading text-2xl font-semibold">Choose a new password</h2>
            <p className="mt-1 text-sm text-ink-muted">Make it at least 8 characters. Mix upper/lower case + a number for best safety.</p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">New password</Label>
                <div className="relative mt-1.5">
                  <Input
                    data-testid="reset-pwd-input"
                    type={show ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="Min 8 characters"
                    autoFocus
                    required
                  />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-subtle hover:text-ink">
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {pwd && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex flex-1 gap-1">
                      {[1,2,3,4,5].map((i) => (
                        <span
                          key={i}
                          className={`h-1 flex-1 rounded-full ${
                            i <= strength.score
                              ? strength.score <= 2 ? "bg-rose-400" : strength.score === 3 ? "bg-amber-400" : "bg-leaf-500"
                              : "bg-border"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-[10px] font-medium text-ink-muted">{strength.label}</span>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Confirm password</Label>
                <Input
                  data-testid="reset-pwd2-input"
                  type={show ? "text" : "password"}
                  value={pwd2}
                  onChange={(e) => setPwd2(e.target.value)}
                  placeholder="Type it again"
                  className="mt-1.5"
                  required
                />
                {pwd && pwd2 && pwd !== pwd2 && <div className="mt-1 text-[10px] text-rose-600">Passwords don't match.</div>}
              </div>
              {error && <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive" data-testid="reset-error">{error}</div>}
              <Button data-testid="reset-submit" type="submit" disabled={submitting || !token} className="w-full bg-brand hover:bg-brand-700">
                {submitting ? <><Loader2 size={14} className="mr-2 animate-spin" /> Updating…</> : <><ShieldCheck size={14} className="mr-2" /> Update password</>}
              </Button>
            </form>
          </>
        )}

        <div className="mt-8 text-center text-[11px] text-ink-muted">
          <Link to="/login" className="hover:text-brand">← Back to sign in</Link>
        </div>
      </Card>
    </div>
  );
}
